import { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Bot,
  Menu,
  X,
  LogOut,
} from 'lucide-react';
import { Dashboard, BotActivity, Login, ForgotPassword, ResetPassword, Forbidden } from './pages';
import { DropletProvider, useDroplet, DROPLETS } from './context/DropletContext';
import { AuthProvider, useAuth } from './context/AuthContext';

function Navigation() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedDroplet, setSelectedDroplet } = useDroplet();
  const { logout } = useAuth();

  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/bot-activity', label: 'AI Bot Activity', icon: Bot },
  ];

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Mobile menu button */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="lg:hidden fixed top-4 right-4 z-50 p-2 bg-dark-800 rounded-lg border border-dark-700 text-white"
      >
        {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </button>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-dark-900 border-r border-dark-700 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-dark-700">
            <div className="flex items-center gap-3 mb-2">
              <img src="/logo.png" alt="3pplea Console" className="h-10 w-auto" />
              <h1 className="text-2xl font-bold text-white">3pplea Console</h1>
            </div>
            <p className="text-sm text-dark-400 mt-1">System Monitoring</p>
          </div>

          {/* Droplet Selector */}
          <div className="p-4 border-b border-dark-700">
            <p className="text-xs text-dark-500 uppercase tracking-wider mb-2">Droplet</p>
            <div className="flex gap-2">
              {DROPLETS.map((droplet) => (
                <button
                  key={droplet.id}
                  onClick={() => {
                    setSelectedDroplet(droplet);
                    setMobileMenuOpen(false);
                  }}
                  title={`${droplet.ip} — ${droplet.description}`}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                    selectedDroplet.id === droplet.id
                      ? 'bg-primary-600 text-white'
                      : 'bg-dark-800 text-dark-400 hover:bg-dark-700 hover:text-white'
                  }`}
                >
                  {droplet.name}
                </button>
              ))}
            </div>
            <p className="text-xs text-dark-500 mt-2">
              {selectedDroplet.ip} — {selectedDroplet.description}
            </p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    active
                      ? 'bg-primary-600 text-white'
                      : 'text-dark-400 hover:bg-dark-800 hover:text-white'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-dark-700 space-y-3">
            <button
              onClick={() => void logout()}
              className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-dark-400 hover:bg-dark-800 hover:text-white transition-colors text-sm font-medium"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
            <div className="text-xs text-dark-500">
              <div>3pplea Infra</div>
              <div className="mt-1">v1.0.0</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </>
  );
}

function AppContent() {
  const { status } = useAuth();

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-dark-950 flex items-center justify-center">
        <div className="text-dark-400 text-sm">Loading…</div>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return (
      <Routes>
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  return (
    <div className="min-h-screen bg-dark-950">
      <Navigation />

      {/* Main content */}
      <main className="lg:pl-64 p-4 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/bot-activity" element={<BotActivity />} />
            <Route path="/forbidden" element={<Forbidden />} />
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

function App() {
  return (
    <Router>
      <DropletProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </DropletProvider>
    </Router>
  );
}

export default App;
